import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Award, Clock } from 'lucide-react';

const values = [
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We stand behind our work. Every project is completed with honesty, transparency, and accountability.',
  },
  {
    icon: Users,
    title: 'Care',
    description: 'We treat every project as if it were our own. Your success is our success.',
  },
  {
    icon: Award,
    title: 'Quality',
    description: 'No shortcuts. No compromises. Just clean, code-compliant work that stands the test of time.',
  },
  {
    icon: Clock,
    title: 'Discipline',
    description: 'On time. On budget. Every time. We manage schedules with military precision.',
  },
];

export default function AboutPage() {
  return (
    <main className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <p className="section-label mb-4 text-[#F5F1EC]/60">About Us</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Builders who <span className="italic text-[#B8A066]">care.</span>
          </h1>
          <p 
            className="text-lg text-[#F5F1EC]/60 max-w-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            A division of Aozat General Contractors, serving the Greater Toronto Area since 2014.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="grid grid-cols-1 lg:grid-cols-2">
        <div className="h-[400px] lg:h-auto">
          <img 
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80"
            alt="Construction"
            className="w-full h-full object-cover grayscale"
          />
        </div>
        <div className="bg-[#F5F1EC] flex items-center">
          <div className="p-12 md:p-16 lg:p-20">
            <p className="section-label mb-6">Our Story</p>
            <h2 
              className="text-3xl md:text-4xl text-[#3D3A38] mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              Where trust meets <span className="italic text-[#B8A066]">quality.</span>
            </h2>
            <div className="space-y-4 text-[#5C5855]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <p className="leading-relaxed">
                Aozat Electrical is the dedicated electrical division of Aozat General Contractors, 
                a construction management practice that has been delivering commercial, residential, 
                and industrial projects across the Greater Toronto Area since 2014.
              </p>
              <p className="leading-relaxed">
                We believe that electrical work is the backbone of any building. It's the system 
                that powers everything else. That's why we approach every project with the same 
                level of care, expertise, and attention to detail—whether it's a custom home, 
                a retail fit-out, or an industrial facility.
              </p>
              <p className="leading-relaxed">
                Our team consists of licensed electricians and project managers who understand 
                that quality work comes from a combination of technical excellence and genuine 
                care for the client's success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="section-label mb-4">Our Values</p>
            <h2 
              className="text-3xl md:text-4xl text-[#3D3A38]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              What drives us <span className="italic text-[#B8A066]">forward.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-[#B8A066]/30">
                  <value.icon size={28} className="text-[#B8A066]" />
                </div>
                <h3 
                  className="text-xl text-[#3D3A38] mb-3"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {value.title}
                </h3>
                <p 
                  className="text-sm text-[#5C5855] leading-relaxed"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p 
                className="text-4xl md:text-5xl text-[#F5F1EC] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}
              >
                2014
              </p>
              <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/50" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Established
              </p>
            </div>
            <div>
              <p 
                className="text-4xl md:text-5xl text-[#F5F1EC] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}
              >
                240+
              </p>
              <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/50" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Projects Delivered
              </p>
            </div>
            <div>
              <p 
                className="text-4xl md:text-5xl text-[#F5F1EC] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}
              >
                $10M+
              </p>
              <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/50" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Active Pipeline
              </p>
            </div>
            <div>
              <p 
                className="text-4xl md:text-5xl text-[#F5F1EC] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}
              >
                24/7
              </p>
              <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/50" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Emergency Service
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom text-center">
          <h2 
            className="text-3xl md:text-4xl text-[#3D3A38] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Ready to work with us?
          </h2>
          <p 
            className="text-[#5C5855] mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Whether you're planning a new project or need maintenance support, we're here to help.
          </p>
          <Link 
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="about-cta"
          >
            Get in Touch <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
